import React, { useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Container, Graphics, useApp } from '@pixi/react';
import { loadingStore } from '../../../store/loadingStore';
import { isMobile } from '../../../core/helpers/isMobile';
import { gameDrawer } from '../../../services/gameDrawer';
import WebView from '../../../view/web';
import MobileView from '../../../view/mobile';
import { getGameImages } from '../../../core/helpers/getGameImages';
import { iframeService } from '../../../services/iframeService';
import { triggerHotJarEvent } from '../../../core/helpers/triggerHotjarEvent';
import { reelContainerParams, symbolSizes } from '../../../core/constants/reelConstants';
import IntroBackground from '../../../components/common/introContent/introBackground';
import IntroContentWrapper from '../introContentWrapper';
import ScaleComponent from '../../../components/mobile/scaleComponent';
import WinLineWrapper from '../winLineWrapper';
import GameBoardWrapper from '../gameBoardWrapper';
import GameLogoWrapper from '../gameLogoWrapper';
import { introService } from '../../../services/introService';
import { userSettingsStore } from '../../../store/userSettingsStore';
import { userInfoStore } from '../../../store/userInfoStore';

const GameViewWrapper = observer(() => {
  const { ticker } = useApp();
  const touchSpinScreenRef = useRef();
  const reelContainerRef = useRef();
  const winContainerRef = useRef();
  const [mask, setMask] = React.useState(null);

  const { isReplay } = userInfoStore;
  const { isShowIntro } = userSettingsStore;
  const { hasGameInitialState } = loadingStore;
  const { maskSprite } = useMemo(() => {
    return getGameImages();
  }, []);

  useEffect(() => {
    if (hasGameInitialState) {
      gameDrawer.execute(
        reelContainerRef.current,
        winContainerRef.current,
        touchSpinScreenRef.current,
        ticker
      );
      if (isShowIntro && !isReplay) {
        introService.startIntro(true);
      }
    }
  }, [isReplay, hasGameInitialState, isShowIntro, ticker]);

  useEffect(() => {
    if (reelContainerRef.current) {
      reelContainerRef.current.mask = mask;
    }
  }, [mask, reelContainerRef]);

  useEffect(() => {
    iframeService.sendGameIsReady();
    triggerHotJarEvent('Game is open');
  }, []);

  const { reelContainerConfig } = useMemo(
    () => ({
      reelContainerConfig: {
        x: -(reelContainerParams.width - symbolSizes.width) / 2,
        y: -(reelContainerParams.height - symbolSizes.height) / 2,
        width: reelContainerParams.width,
        height: reelContainerParams.height,
      },
    }),
    []
  );
  const { touchSpinScreenConfig } = useMemo(() => {
    return {
      touchSpinScreenConfig: {
        x: reelContainerParams.xInFigma + reelContainerParams.offsetForContainerBoardWidth,
        y: reelContainerParams.yInFigma + reelContainerParams.offsetForContainerBoardHeight,
        width: reelContainerParams.width,
        height: reelContainerParams.height,
      },
    };
  }, []);
  const commonContent = useMemo(
    () => (
      <>
        <GameLogoWrapper />
        <GameBoardWrapper
          setMask={setMask}
          winContainerRef={winContainerRef}
          reelContainerRef={reelContainerRef}
          reelContainerConfig={reelContainerConfig}
        />
        <WinLineWrapper />
        <Graphics
          draw={(g) => {
            g.clear();
            g.alpha = 0;
            g.interactive = true;
            g.beginFill(0x030303);
            g.drawRect(0, 0, isMobile() ? 540 : 1440, isMobile() ? 960 : 810);
          }}
        />
        <IntroContentWrapper />
      </>
    ),
    [setMask, winContainerRef, reelContainerRef, reelContainerConfig]
  );

  return isShowIntro && !isReplay ? (
    <Container>
      <IntroBackground />
      {isMobile() ? (
        <ScaleComponent isCenterHorizontal childrenDefaultWidth={540}>
          {commonContent}
        </ScaleComponent>
      ) : (
        commonContent
      )}
    </Container>
  ) : isMobile() ? (
    <MobileView
      setMask={setMask}
      reelContainerRef={reelContainerRef}
      winContainerRef={winContainerRef}
      touchSpinScreenRef={touchSpinScreenRef}
      reelContainerConfig={reelContainerConfig}
      touchSpinScreenConfig={touchSpinScreenConfig}
    />
  ) : (
    <WebView
      setMask={setMask}
      maskSprite={maskSprite}
      reelContainerRef={reelContainerRef}
      winContainerRef={winContainerRef}
      touchSpinScreenRef={touchSpinScreenRef}
      reelContainerConfig={reelContainerConfig}
      touchSpinScreenConfig={touchSpinScreenConfig}
    />
  );
});
export default GameViewWrapper;
